import AbstractAccountGroupVisitor from "../../../chartOfAccounts/AbstractAccountGroupVisitor.js";
import GeneratedAccountGroupRowTemplate from "./GeneratedAccountGroupRowTemplate.js";
import GeneratedAccountRowTemplate from "./GeneratedAccountRowTemplate.js";

/**
 * A chart of accounts group visitor returning generated row
 * templates. The returned row templates mimic the structure of the
 * tree of the visited account group.
 *
 * @param {Account[]} accounts - All accounts of the interpretation
 * context. Only accounts owned by the visited account group will be
 * used: each of these accounts will be transformed into a row
 * template.
 * @param {AbstractAccountGroupRowTemplate} parent - The row template
 * that wants child rows for its account groups.
 */
export default class AccountGroupRowTemplateCreator extends AbstractAccountGroupVisitor {
	constructor({ accounts, parent }) {
		super(...arguments);

		this._accounts = accounts;
		this._parent = parent;
	}

	visitAbstractAccountGroup(abstractAccountGroup) {
		const accountGroupAccounts = this._accounts
			.filter((account) => abstractAccountGroup.hasAccount(account))
			.sort((account1, account2) =>
				account1.getNumber() > account2.getNumber() ? 1 : -1,
			);

		return GeneratedAccountRowTemplate.makeRowTemplatesFromAccounts({
			accounts: accountGroupAccounts,
			parent: this._parent,
		});
	}

	visitAccountGroup(accountGroup) {
		if (!accountGroup.hasAccountGroupChildren()) {
			return this._visitChildren(accountGroup);
		}

		return GeneratedAccountGroupRowTemplate.makeRowTemplatesFromAccountGroups(
			{
				accountGroups: accountGroup.getChildren(),
				parent: this._parent,
			},
		);
	}

	visitAccountGroupAliasing(accountGroupAliasing) {
		return accountGroupAliasing.getAliasedAccountGroup().accept(this);
	}

	_visitChildren(accountGroup) {
		return accountGroup
			.getChildren()
			.flatMap((child) => child.accept(this));
	}
}
